import React, { useRef, useEffect, useState } from "react";
import Chart from "chart.js";
import s from "./CobWeb.less";
import {
  useGetCheckListIndicatorStatsLazyQuery,
  useGetDealerQuery,
} from "~/schemas/generated/graphql";
import { useCurrentDealerId } from "~/shared/hooks/useCurrentDealerId";
import { useTranslation } from "react-i18next";
import DealerInfoPanel from "../DealerInfoPanel";
import { PkdLinks } from '~/widgets/PkdLinks';

const CobWeb = () => {
  const currentDealerId = useCurrentDealerId();
  const { t } = useTranslation();
  const { data: dealerData, loading: dealerLoading } = useGetDealerQuery({
    variables: {
      DealerId: currentDealerId,
    },
    fetchPolicy: "no-cache",
  });

  const [checkListIndicatorData, { loading: indicatorLoading, error, data }] =
    useGetCheckListIndicatorStatsLazyQuery({
      fetchPolicy: "no-cache",
    });

  const [isEmpty, setIsEmpty] = useState(false);

  useEffect(() => {
    if (dealerData?.DealerGetter?.getDealer?.autoCRMID) {
      checkListIndicatorData({
        variables: {
          dealerAutoCrmid: dealerData.DealerGetter.getDealer.autoCRMID,
        },
      });
    }
  }, [dealerData, checkListIndicatorData]);

  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scale: {
      ticks: {
        beginAtZero: true,
        min: 0,
        max: 100,
        stepSize: 10,
      },
      gridLines: {
        color: "#e4e4e4",
      },
      angleLines: {
        color: "#e4e4e4",
      },
    },
    legend: {
      display: false,
    },
    tooltips: {
      enabled: true,
      callbacks: {
        label: (tooltipItem, data) => {
          const dataset = data.datasets[tooltipItem.datasetIndex].label || "";
          const value = tooltipItem.yLabel;
          return `${dataset}: ${value.toFixed(0)}%`;
        },
      },
    },
  };

  useEffect(() => {
    if (chartRef.current && !indicatorLoading && data) {
      const stats = data.CheckListIndicatorGetter.getCheckListIndicatorStats;
      if (stats.length === 0) {
        setIsEmpty(true);
        return;
      } else {
        setIsEmpty(false);
      }

      const scaledValues = stats.map((item) => ({
        name: item.name,
        value: item.value,
      }));

      const scaledData = {
        labels: scaledValues.map((item) => item.name),
        datasets: [
          {
            label: "",
            data: scaledValues.map((item) => item.value),
            backgroundColor: "rgba(26,149,251,0.2)",
            borderColor: "#1a95fb",
            pointRadius: 0,
            pointHoverRadius: 4,
          },
        ],
      };

      new Chart(chartRef.current, {
        type: "radar",
        data: scaledData,
        options,
      });
    }
  }, [data, indicatorLoading]);

  return (
    <div className={s.wrapperChart}>
      <PkdLinks />
      <DealerInfoPanel type="Staff" />
      {isEmpty ? (
        <div className={`${s.noData}` + " " + `${s.marginTop}`}>
          {t(`pkd_indicators.no_data`)}
        </div>
      ) : (
        <canvas ref={chartRef} width={400} height={400}></canvas>
      )}
    </div>
  );
};

export default CobWeb;
